'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import Navbar from '../components/Navbar'
import { useAuth } from '../contexts/AuthContext'
import { formatTimeString } from '@/lib/time-utils'

interface Location {
  id: number
  city: string
  name: string
  address: string
  timeWindowStart?: string
  timeWindowEnd?: string
  type: 'pickup' | 'dropoff'
}

export default function RiderPage() {
  const router = useRouter()
  const { user, isLoading } = useAuth()
  // Redirect based on user type
  useEffect(() => {
    if (!isLoading && user) {
      if (user.type === 'student') {
        router.push('/student/orders')
      } else if (user.type === 'parent') {
        router.push('/rider/new-order')
      }
      // If not logged in or other type, stay on this page
    } else if (!isLoading && !user) {
      router.push('/login')
    }
  }, [user, isLoading, router])

  const [pickupLocations, setPickupLocations] = useState<Location[]>([
    { 
      id: 1, 
      city: 'Frisco',
      name: 'Heritage High School', 
      address: '14040 Eldorado Pkwy, Frisco, TX', 
      timeWindowStart: '11:00',
      timeWindowEnd: '11:30',
      type: 'pickup' 
    },
    { 
      id: 2, 
      city: 'Plano',
      name: 'Walmart', 
      address: '8040 Independence Pkwy, Plano, TX', 
      timeWindowStart: '11:45',
      timeWindowEnd: '12:00',
      type: 'pickup' 
    },
    { 
      id: 3, 
      city: 'Irving',
      name: "Kohl's", 
      address: '1035 Market Pl Blvd, Irving, TX', 
      timeWindowStart: '12:40',
      timeWindowEnd: '13:00',
      type: 'pickup' 
    },
  ])

  const [dropoffLocations, setDropoffLocations] = useState<Location[]>([
    {
      id: 4,
      city: 'UT Austin',
      name: 'Jester West',
      address: 'Between Jester West and Brazos Garage on Jester side of the Street',
      timeWindowStart: '14:00',
      timeWindowEnd: '16:00',
      type: 'dropoff'
    },
    {
      id: 5,
      city: 'UT Austin',
      name: '26 West Apartments',
      address: 'Nueces on 26 West side of the street',
      timeWindowStart: '14:00',
      timeWindowEnd: '16:00',
      type: 'dropoff'
    },
  ])

  const updateTimeWindow = (id: number, type: 'pickup' | 'dropoff', field: 'timeWindowStart' | 'timeWindowEnd', value: string) => {
    if (type === 'pickup') {
      setPickupLocations(pickupLocations.map(loc => 
        loc.id === id ? { ...loc, [field]: value } : loc
      ))
    } else {
      setDropoffLocations(dropoffLocations.map(loc => 
        loc.id === id ? { ...loc, [field]: value } : loc
      ))
    }
  }

  // Show loading while checking auth or redirecting
  if (isLoading || (user && (user.type === 'student' || user.type === 'parent'))) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-cream">
      <Navbar activeTab="rider" />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-8 py-12">
        <h1 className="text-4xl font-serif font-semibold tracking-tight text-ink mb-8 flex items-center gap-3">
          <span className="text-4xl">🍽️</span>
          <span>Book Your Food Delivery</span>
        </h1>

        {/* Pickup Locations */}
        <div className="mb-12">
          <h2 className="text-2xl font-display text-gray-900 mb-6 flex items-center gap-2">
            <span>📍</span>
            <span>Pickup Locations</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pickupLocations.map((location) => (
              <div
                key={location.id}
                className="bg-white rounded-2xl p-6 shadow-sm border border-cream-deep hover:border-ember/30 transition"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-ember-soft text-ember border border-ember/20">
                    Pickup
                  </span>
                  <span className="text-gray-600 text-sm font-medium">{location.city}</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm mb-2 font-medium">
                      Location Name
                    </label>
                    <div className="w-full bg-gray-50 text-gray-900 px-4 py-3 rounded-lg border border-gray-200">
                      <div className="font-semibold">{location.name}</div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm mb-2 font-medium">
                      Address
                    </label>
                    <div className="w-full bg-gray-50 text-gray-700 px-4 py-3 rounded-lg border border-gray-200">
                      {location.address}
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm mb-2 font-medium">
                      Pickup Time Window
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <input
                          type="time"
                          value={location.timeWindowStart || ''}
                          onChange={(e) => updateTimeWindow(location.id, 'pickup', 'timeWindowStart', e.target.value)}
                          className="w-full bg-white text-gray-900 px-3 py-2 rounded-lg text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember"
                        />
                      </div>
                      <div>
                        <input
                          type="time"
                          value={location.timeWindowEnd || ''}
                          onChange={(e) => updateTimeWindow(location.id, 'pickup', 'timeWindowEnd', e.target.value)}
                          className="w-full bg-white text-gray-900 px-3 py-2 rounded-lg text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember"
                        />
                      </div>
                    </div>
                    {(location.timeWindowStart || location.timeWindowEnd) && (
                      <div className="mt-2 text-sm text-gray-600">
                        ⏰ {formatTimeString(location.timeWindowStart || '')} – {formatTimeString(location.timeWindowEnd || '')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dropoff Locations */}
        <div className="mb-8">
          <h2 className="text-2xl font-display text-gray-900 mb-6 flex items-center gap-2">
            <span>🎯</span>
            <span>Dropoff Locations</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {dropoffLocations.map((location) => (
              <div
                key={location.id}
                className="bg-white rounded-2xl p-6 shadow-sm border border-cream-deep hover:border-green-300 transition"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700 border border-green-300">
                    Dropoff
                  </span>
                  <span className="text-gray-600 text-sm font-medium">{location.city}</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm mb-2 font-medium">
                      Location Name
                    </label>
                    <div className="w-full bg-gray-50 text-gray-900 px-4 py-3 rounded-lg border border-gray-200">
                      <div className="font-semibold">{location.name}</div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm mb-2 font-medium">
                      Address / Instructions
                    </label>
                    <div className="w-full bg-gray-50 text-gray-700 px-4 py-3 rounded-lg border border-gray-200">
                      {location.address}
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm mb-2 font-medium">
                      Dropoff Time Window
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <input
                          type="time"
                          value={location.timeWindowStart || ''}
                          onChange={(e) => updateTimeWindow(location.id, 'dropoff', 'timeWindowStart', e.target.value)}
                          className="w-full bg-white text-gray-900 px-3 py-2 rounded-lg text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember"
                        />
                      </div>
                      <div>
                        <input
                          type="time"
                          value={location.timeWindowEnd || ''}
                          onChange={(e) => updateTimeWindow(location.id, 'dropoff', 'timeWindowEnd', e.target.value)}
                          className="w-full bg-white text-gray-900 px-3 py-2 rounded-lg text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember"
                        />
                      </div>
                    </div>
                    {(location.timeWindowStart || location.timeWindowEnd) && (
                      <div className="mt-2 text-sm text-gray-600">
                        ⏰ {formatTimeString(location.timeWindowStart || '')} – {formatTimeString(location.timeWindowEnd || '')}
                      </div>
                    )}
                  </div>

                </div>
              </div>
            ))}
          </div>
        </div>


        <div className="mt-8 flex justify-end">
          <Link
            href="/rider/new-order"
            className="bg-ember text-white px-8 py-4 rounded-full font-semibold hover:bg-ember-hover transition-colors shadow-md shadow-ember/25 flex items-center space-x-2 min-h-[44px]"
          >
            <span>Create New Order</span>
            <span className="text-xl">→</span>
          </Link>
        </div>
      </div>
    </div>
  )
}
